import { getAuthCooldownRemainingMs } from 'serviceWorker/services/config';
import { rateLimitManager } from 'serviceWorker/api/RateLimitManager';
import { debugLog } from 'shared/logger/debugLog';

/** Max auth-cooldown retries before giving up (~5 min at 30s cooldown). */
export const AUTH_COOLDOWN_MAX_RETRIES = 10;

const pendingTimers = new Map<string, ReturnType<typeof setTimeout>>();

export type AuthRetryBudget = {
  attempts: number;
  maxAttempts: number;
};

export function createAuthRetryBudget(
  maxAttempts = AUTH_COOLDOWN_MAX_RETRIES
): AuthRetryBudget {
  return { attempts: 0, maxAttempts };
}

export function scheduleDelayedRetry(
  key: string,
  delayMs: number,
  retry: () => void
): boolean {
  if (delayMs <= 0) {
    return false;
  }

  const existing = pendingTimers.get(key);
  if (existing !== undefined) {
    return false;
  }

  pendingTimers.set(
    key,
    setTimeout(() => {
      pendingTimers.delete(key);
      retry();
    }, delayMs)
  );

  return true;
}

const authCooldownTimerKey = (retryKey: string): string => `auth-cooldown:${retryKey}`;

/** Whether a retry after the auth cooldown is already scheduled for `retryKey`. */
export function hasScheduledAuthCooldownRetry(retryKey: string): boolean {
  return pendingTimers.has(authCooldownTimerKey(retryKey));
}

/**
 * Retries once the auth cooldown ends. Nothing to wait for without a cooldown
 * (`false`), and the budget counts only the retries that were scheduled: calls
 * that found no cooldown must not use it up before a real one comes.
 */
export function scheduleAfterAuthCooldown(
  retryKey: string,
  retry: () => void,
  budget?: AuthRetryBudget
): boolean {
  const remainingMs = getAuthCooldownRemainingMs();
  if (remainingMs <= 0) {
    return false;
  }
  if (budget !== undefined && budget.attempts >= budget.maxAttempts) {
    debugLog('auth cooldown retry budget exhausted', { retryKey, ...budget });
    return false;
  }

  const isScheduled = scheduleDelayedRetry(authCooldownTimerKey(retryKey), remainingMs, retry);
  if (isScheduled && budget !== undefined) {
    budget.attempts += 1;
  }
  return isScheduled;
}

export function scheduleAfterRateLimit(
  retryKey: string,
  retry: () => void
): boolean {
  return scheduleDelayedRetry(
    `rate-limit:${retryKey}`,
    rateLimitManager.remainingMs(),
    retry
  );
}

/** test-only */
export function clearScheduledSwRetriesForTests(): void {
  pendingTimers.forEach((timer) => clearTimeout(timer));
  pendingTimers.clear();
}

